import {
    cancelSelectEvent,
    getCSSStyles,
    getEChartsTooltipTheme,
    getHeaderPinButtonHTML,
    getHeaderPinStateClass,
    getWidgetDataURI,
    normalizeWidgetTheme,
    setupHeaderPin,
    toggleWidgetTheme
} from '../../helpers';
import { FinancialConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

const SECTORS = [
    'Medical Specialities',
    'Automobiles & Auto Parts',
    'Utilities Regulated',
    'Finance/Rental/Leasing',
    'Banking Services',
    'Software - Services',
    'GAS Distribution',
    'Independent Power Producers',
    'Retail/Apparel/Specialty',
    'Real Estate Investment Trust'
] as const;

const ESG_ROWS = ['Environmental', 'Social', 'Governance', 'ESG Score'] as const;

/** Dummy ESG scores [row][column] — values 0–100 mapped to the red→green scale. */
const DUMMY_MATRIX: number[][] = [
    [18, 22, 38, 58, 72, 85, 52.3, 15, 42, 78],
    [82, 45, 62, 88, 12, 91, 18, 35, 55, 8],
    [90, 75, 68, 52, 38, 88, 85, 72, 65, 40],
    [25, 35, 48, 22, 15, 92, 20, 45, 50, 18]
];

const ESG_COLOR_SCALE = [
    '#B81E1E',
    '#D9402A',
    '#E86A1A',
    '#F0A01A',
    '#E8C41A',
    '#C5D63A',
    '#7AC74F',
    '#33D17A'
];

type HeatmapCell = [number, number, number];

export class TraderverseESGSectorsPerformanceMatrix {
    private container: HTMLElement;
    private config: FinancialConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private currentChart: any = null;
    private widgetResizeObserver: ResizeObserver | null = null;

    constructor(config: FinancialConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<FinancialConfig> {
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        } as any;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        this.updateDisplay();
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
   <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} ESG Sectors Performance Matrix</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="chartDv" id="esgSectorsMatrix" data-testing-id="esg-sectors-performance-matrix">
                <div class="esgMatrixScale">
                    <div class="esgMatrixScaleTicks" id="esgMatrixScaleTicks"></div>
                    <div class="esgMatrixScaleBar"></div>
                </div>
                <div id="chartContainer" class="chart esgMatrixChart"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        setupHeaderPin(this.shadowRoot, this.config);
        this.renderScaleLegend();
    }

    private buildHeatmapData(): HeatmapCell[] {
        const data: HeatmapCell[] = [];
        DUMMY_MATRIX.forEach((row, rowIndex) => {
            row.forEach((value, colIndex) => {
                data.push([colIndex, rowIndex, value]);
            });
        });
        return data;
    }

    private renderScaleLegend(): void {
        const ticksEl = this.shadowRoot.querySelector('#esgMatrixScaleTicks') as HTMLElement | null;
        if (!ticksEl) return;
        const ticks = [0, 20, 40, 60, 80, 100];
        ticksEl.innerHTML = ticks.map((tick) => `<span>${tick}</span>`).join('');
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
            this.injectMatrixStyles();
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private injectMatrixStyles(): void {
        const existing = this.shadowRoot.querySelector('style[data-esg-matrix-scale]');
        if (existing) existing.remove();

        const style = document.createElement('style');
        style.setAttribute('data-esg-matrix-scale', 'true');
        style.textContent = `
            #esgSectorsMatrix.chartDv {
                height: auto;
                min-height: 400px;
                display: flex;
                flex-direction: column;
                flex: 0 0 auto;
                overflow: visible;
            }
            .esgMatrixScale {
                width: 100%;
                max-width: 420px;
                padding: 4px 0 12px;
                box-sizing: border-box;
                flex: 0 0 auto;
            }
            .esgMatrixScaleBar {
                width: 100%;
                height: 12px;
                border-radius: 6px;
                background: linear-gradient(90deg, #B81E1E 0%, #D9402A 14%, #E86A1A 28%, #F0A01A 42%, #E8C41A 57%, #C5D63A 71%, #7AC74F 85%, #33D17A 100%);
            }
            .esgMatrixScaleTicks {
                display: flex;
                justify-content: space-between;
                align-items: center;
                width: 100%;
                margin-bottom: 6px;
                color: var(--charts-text-color, #9A95AC);
                font-size: var(--font-size-sm, 12px);
                line-height: 1;
            }
            .esgMatrixScaleTicks span:first-child {
                text-align: left;
            }
            .esgMatrixScaleTicks span:last-child {
                text-align: right;
            }
            .esgMatrixScaleTicks span {
                flex: 1;
                text-align: center;
            }
            .esgMatrixScaleTicks span:first-child,
            .esgMatrixScaleTicks span:last-child {
                flex: 0 0 auto;
            }
            #esgSectorsMatrix .esgMatrixChart {
                position: relative !important;
                left: auto !important;
                top: auto !important;
                width: 100%;
                height: 320px;
                flex: 0 0 auto;
                min-height: 320px;
            }
            .widgetBox:has(#esgSectorsMatrix) .inBody {
                height: auto;
                min-height: 400px;
                overflow: visible;
            }
        `;
        this.shadowRoot.appendChild(style);
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private updateDisplay(): void {
        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawChart();
    }

    private formatScore(value: number): string {
        return Number.isInteger(value) ? String(value) : value.toFixed(1);
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
        if (!container) return;

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        container.innerHTML = '';

        const chartHeight = 320;
        container.style.height = `${chartHeight}px`;
        container.style.minHeight = `${chartHeight}px`;
        container.style.width = '100%';

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const widgetStyles = getComputedStyle(widgetBox);
        const fontSize = widgetStyles.getPropertyValue('--font-size-sm').trim() || '12px';
        const fontSizeMd = widgetStyles.getPropertyValue('--font-size-md').trim() || '14px';
        const textColor = widgetStyles.getPropertyValue('--charts-text-color').trim() || '#9A95AC';

        const heatmapData = this.buildHeatmapData();

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                position: 'top',
                confine: true,
                appendToBody: false,
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    const [colIndex, , value] = params.data as HeatmapCell;
                    const sector = SECTORS[colIndex];
                    return `
                    <div style="font-size:${fontSize}; line-height:1.45;">
                        <div style="font-size:${fontSizeMd}; font-weight:700; margin-bottom:2px;">${this.formatScore(value)}</div>
                        <div>${sector}</div>
                    </div>
                `;
                }
            },
            grid: {
                left: 8,
                right: 8,
                top: 4,
                bottom: 4,
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: [...SECTORS],
                show: false,
                splitArea: { show: false }
            },
            yAxis: {
                type: 'category',
                data: [...ESG_ROWS],
                inverse: true,
                splitArea: { show: false },
                axisLabel: {
                    fontSize,
                    color: textColor,
                    margin: 12
                },
                axisTick: { show: false },
                axisLine: { show: false }
            },
            visualMap: {
                min: 0,
                max: 100,
                calculable: false,
                show: false,
                inRange: {
                    color: ESG_COLOR_SCALE
                }
            },
            series: [
                {
                    name: 'ESG Score',
                    type: 'heatmap',
                    data: heatmapData,
                    label: {
                        show: true,
                        formatter: (params: any) => {
                            const [colIndex, rowIndex] = params.data as HeatmapCell;
                            if (ESG_ROWS[rowIndex] === 'Social') return SECTORS[colIndex];
                            return '';
                        },
                        color: '#fff',
                        fontSize: 10,
                        rotate: 90,
                        align: 'center',
                        verticalAlign: 'middle'
                    },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 8,
                            shadowColor: 'rgba(0, 0, 0, 0.45)'
                        }
                    },
                    itemStyle: {
                        borderColor: '#0b0b1a',
                        borderWidth: 2
                    }
                }
            ],
            animation: true,
            animationDuration: 800,
            animationEasing: 'cubicOut'
        };

        this.currentChart = echarts.init(container, null, { renderer: 'canvas' });
        this.currentChart.setOption(option, true);
        this.currentChart.resize({ width: container.clientWidth || undefined, height: chartHeight });

        requestAnimationFrame(() => {
            this.currentChart?.resize({ width: container.clientWidth || undefined, height: chartHeight });
        });
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter((h) => h !== handler));
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }
    public destroy(): void {
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach((h) => h(evt));
        }
    }
    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global {
    interface Window {
        TraderverseESGSectorsPerformanceMatrix: typeof TraderverseESGSectorsPerformanceMatrix;
    }
}
if (typeof window !== 'undefined') {
    window.TraderverseESGSectorsPerformanceMatrix = TraderverseESGSectorsPerformanceMatrix;
}
